/**
 * Helpers for the {@link TimeSelector} component: duration <-> H/M/S conversion,
 * preset resolution and label formatting. Clock/duration formatting reuses the
 * shared dayjs `duration` plugin (registered globally in `main.tsx`); the preset
 * and nearest-period logic is domain-specific and stays here.
 */

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import { getBetPeriodId, getBetPeriodSeconds } from "@/types/index";
import type { TradingPeriodMessage } from "@cronika/proto/common/trading_pb";
import {
  DURATION_MAX_SECONDS,
  DURATION_MIN_SECONDS,
  TIME_QUICK_COUNT,
  TIME_QUICK_STEP_MINUTES,
} from "./config";

dayjs.extend(duration);

export type THms = { hours: number; minutes: number; seconds: number };

export const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export const pad2 = (value: number): string => String(Math.floor(value)).padStart(2, "0");

/** Split a total number of seconds into hours / minutes / seconds. */
export const secondsToHms = (total: number): THms => {
  const d = dayjs.duration(Math.max(0, Math.floor(total)), "seconds");
  return { hours: Math.floor(d.asHours()), minutes: d.minutes(), seconds: d.seconds() };
};

/** Recombine hours / minutes / seconds into a total number of seconds. */
export const hmsToSeconds = ({ hours, minutes, seconds }: THms): number => hours * 3600 + minutes * 60 + seconds;

/** Extract H/M/S from a Date in the given IANA timezone (falls back to local when tz is empty). */
export const getHmsInTimezone = (date: Date, tz: string): THms => {
  if (!tz) return { hours: date.getHours(), minutes: date.getMinutes(), seconds: date.getSeconds() };
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: string) => {
    const v = parseInt(parts.find((p) => p.type === type)?.value ?? "0", 10);
    return type === "hour" && v === 24 ? 0 : v;
  };
  return { hours: get("hour"), minutes: get("minute"), seconds: get("second") };
};

/** Seconds elapsed since midnight in the given timezone. */
export const getSodInTimezone = (date: Date, tz: string): number => {
  const { hours, minutes, seconds } = getHmsInTimezone(date, tz);
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Seconds from `nowSod` until `targetSod` (both seconds-of-day), rolling
 * forward into tomorrow when the target's clock time already passed today.
 * Plain `targetSod - nowSod` breaks whenever "now" sits close to midnight in
 * the selected timezone: a target of 00:05 with now at 23:58 is 7 minutes
 * away, not -23h53m. Bare subtraction reads that as wildly "in the past" —
 * every timezone crosses this window once a day, it's just far more likely
 * to be hit right after switching to a timezone many hours away from the
 * previous one, since the local wall-clock can jump right past midnight.
 */
export const secondsUntilSod = (targetSod: number, nowSod: number): number => {
  const diff = targetSod - nowSod;
  return diff < 0 ? diff + 24 * 3600 : diff;
};

/** True when the wall-clock `hms` is strictly earlier than `now` (seconds-of-day). */
export const isLessThanCurrentTime = (hms: THms, now: Date = new Date(), tz = ""): boolean => {
  const nowSod = getSodInTimezone(now, tz);
  return hmsToSeconds(hms) < nowSod;
};

/** Wall-clock time one minute after `now`. */
export const nowPlusOneMinute = (now: Date = new Date(), tz = ""): THms => {
  const target = new Date(now.getTime() + 60_000);
  return getHmsInTimezone(target, tz);
};

/** `01:05:09` style clock string. */
export const formatClock = (hms: THms): string => dayjs.duration(hms).format("HH:mm:ss");

/**
 * Compact human label for a duration, e.g. `5 s`, `3 m`, `4 h`, `1 h 30 m`.
 * Used both for the compact field summary and the "Quick" preset buttons.
 */
export const formatDurationLabel = (totalSeconds: number): string => {
  const { hours, minutes, seconds } = secondsToHms(totalSeconds);
  const parts: string[] = [];
  if (hours) parts.push(`${hours} h`);
  if (minutes) parts.push(`${minutes} m`);
  if (seconds || parts.length === 0) parts.push(`${seconds} s`);
  return parts.join(" ");
};

export type TDurationPreset = {
  /** Preset length in seconds (from config). */
  seconds: number;
  /** Compact label shown on the button. */
  label: string;
  /** Backing pair bet period, when one matches this length. */
  period?: TradingPeriodMessage;
  /** Stable key for React lists. */
  key: string;
};

/**
 * Build the duration quick-pick presets from the pair's own bet periods.
 * The list is whatever the admin configured on the pair (presetDurations),
 * already sorted and deduplicated by getPairBetPeriods before arriving here.
 */
export const resolveDurationPresets = (betPeriods: TradingPeriodMessage[]): TDurationPreset[] => {
  return betPeriods.map((period) => {
    const seconds = getBetPeriodSeconds(period);
    return {
      seconds,
      label: formatDurationLabel(seconds),
      period,
      key: getBetPeriodId(period),
    };
  });
};

/**
 * Pick the bet period whose length is closest to `targetSeconds`, optionally
 * excluding periods that would outlive the market close. Used to translate a
 * "Custom" duration or an absolute "Time" target into a concrete, priced period
 * so the trade still flows through the server-defined ladder.
 */
export const findClosestPeriod = (
  betPeriods: TradingPeriodMessage[],
  targetSeconds: number,
  marketCloseSeconds?: number | null,
): TradingPeriodMessage | undefined => {
  const candidates = betPeriods.filter(
    (period) => marketCloseSeconds == null || getBetPeriodSeconds(period) < marketCloseSeconds,
  );
  const pool = candidates.length ? candidates : betPeriods;
  if (!pool.length) return undefined;

  return pool.reduce((best, period) => {
    const bestDiff = Math.abs(getBetPeriodSeconds(best) - targetSeconds);
    const diff = Math.abs(getBetPeriodSeconds(period) - targetSeconds);
    return diff < bestDiff ? period : best;
  });
};

export type TTimePreset = {
  /** Target wall-clock time, e.g. `17:30`. */
  label: string;
  /** Seconds from `now` until this target. */
  durationSeconds: number;
  /** Absolute target as ms since epoch. */
  targetMs: number;
  key: string;
};

/**
 * Build the absolute-time "Quick" presets: the next {@link TIME_QUICK_COUNT}
 * wall-clock marks on a {@link TIME_QUICK_STEP_MINUTES} grid, starting at the
 * first mark strictly after `now`. Values are dynamic — computed from the
 * supplied `now` (defaults to the current instant).
 */
export const computeTimeQuickPresets = (now: Date = new Date(), tz = ""): TTimePreset[] => {
  const stepMs = TIME_QUICK_STEP_MINUTES * 60 * 1000;
  const firstMark = Math.ceil((now.getTime() + 1) / stepMs) * stepMs;

  return Array.from({ length: TIME_QUICK_COUNT }, (_, i) => {
    const targetMs = firstMark + i * stepMs;
    const { hours, minutes } = getHmsInTimezone(new Date(targetMs), tz);
    const label = `${pad2(hours)}:${pad2(minutes)}`;
    return {
      label,
      durationSeconds: Math.round((targetMs - now.getTime()) / 1000),
      targetMs,
      key: `time-${targetMs}`,
    };
  });
};

/** Clamp an arbitrary duration into the manual stepper's allowed range. */
export const clampDuration = (seconds: number): number =>
  clamp(seconds, DURATION_MIN_SECONDS, DURATION_MAX_SECONDS);

/**
 * Snap an absolute wall-clock time to the next (`dir: 1`) or previous (`dir: -1`)
 * {@link TIME_QUICK_STEP_MINUTES} mark, always landing strictly beyond the
 * current value (18:26 → 18:30 → 18:45 …). Wraps within a 24h day.
 */
export const stepQuarterHour = (hms: THms, dir: 1 | -1): THms => {
  const stepSec = TIME_QUICK_STEP_MINUTES * 60;
  const total = hmsToSeconds(hms);
  const next = dir === 1 ? (Math.floor(total / stepSec) + 1) * stepSec : (Math.ceil(total / stepSec) - 1) * stepSec;
  return secondsToHms(((next % 86400) + 86400) % 86400);
};

/**
 * Shortest bet period the pair actually offers (the minimum TTL configured in
 * admin). Manual entry is rejected below this: an expiry sooner than the
 * shortest tradable period — or already in the past — is not selectable.
 * Falls back to the stepper's own floor when the pair exposes no periods yet.
 */
export const getMinPeriodSeconds = (betPeriods: TradingPeriodMessage[]): number => {
  const lengths = betPeriods.map(getBetPeriodSeconds).filter((seconds) => seconds > 0);
  return lengths.length ? Math.min(...lengths) : DURATION_MIN_SECONDS;
};
